import React, { createContext, useContext, useState, useEffect } from 'react';

type Mode = 'live' | 'demo';

interface ModeContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
  isDemo: boolean;
  isLive: boolean;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export function ModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<Mode>(() => {
    const stored = localStorage.getItem('app-mode');
    return (stored === 'live' || stored === 'demo') ? stored : 'live';
  });

  useEffect(() => {
    localStorage.setItem('app-mode', mode);
  }, [mode]);

  const setMode = (newMode: Mode) => {
    localStorage.setItem('app-mode', newMode);
    setModeState(newMode);
  };

  const value: ModeContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextType {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}
